// Envío masivo de correos (solo administración).
import apiService from './api';

export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'paused' | 'sent' | 'cancelled';

export interface Audience {
  roles: string[];
  user_ids: number[];
  include_test?: boolean;
}

export interface Campaign {
  id: number;
  name: string;
  subject: string;
  html: string;
  cta_label?: string | null;
  cta_url?: string | null;
  template_id?: number | null;
  use_layout?: number;
  audience: Audience | null;
  status: CampaignStatus;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
  scheduled_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  created_by_name?: string | null;
  created_at: string;
}

export interface CampaignRecipient {
  id: number;
  email: string;
  name: string | null;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  attempts: number;
  last_error: string | null;
  sent_at: string | null;
}

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  html: string;
  cta_label?: string | null;
  cta_url?: string | null;
  description?: string | null;
  use_layout?: number;
}

export interface AudiencePerson {
  user_id: number;
  email: string;
  name: string;
  role: string;
  is_test: number;
}

const emailApi = {
  // Campañas
  list: async (): Promise<Campaign[]> =>
    (await apiService.get('/admin/email/campaigns')).data?.data || [],

  get: async (id: number): Promise<{ campaign: Campaign; recipients: CampaignRecipient[] }> => {
    const r = (await apiService.get(`/admin/email/campaigns/${id}`)).data;
    return { campaign: r.data, recipients: r.recipients || [] };
  },

  create: async (payload: Partial<Campaign>) =>
    (await apiService.post('/admin/email/campaigns', payload)).data,

  update: async (id: number, payload: Partial<Campaign>) =>
    (await apiService.put(`/admin/email/campaigns/${id}`, payload)).data,

  remove: async (id: number) =>
    (await apiService.delete(`/admin/email/campaigns/${id}`)).data,

  sendTest: async (id: number, email: string) =>
    (await apiService.post(`/admin/email/campaigns/${id}/test`, { email })).data,

  send: async (id: number, scheduledAt?: string | null) =>
    (await apiService.post(`/admin/email/campaigns/${id}/send`, { scheduled_at: scheduledAt || null })).data,

  pause: async (id: number) => (await apiService.post(`/admin/email/campaigns/${id}/pause`, {})).data,
  resume: async (id: number) => (await apiService.post(`/admin/email/campaigns/${id}/resume`, {})).data,
  cancel: async (id: number) => (await apiService.post(`/admin/email/campaigns/${id}/cancel`, {})).data,

  // Audiencia y previsualización
  audience: async (audience: Audience): Promise<{ total: number; people: AudiencePerson[] }> =>
    (await apiService.post('/admin/email/audience', audience)).data,

  preview: async (payload: { subject: string; html: string; cta_label?: string | null; cta_url?: string | null; use_layout?: boolean; user_id?: number }):
    Promise<{ subject: string; html: string }> =>
    (await apiService.post('/admin/email/preview', payload)).data,

  // Trae un HTML externo y rehospeda sus imágenes en el servidor.
  importHtml: async (html: string): Promise<{ html: string; imported: number; skipped: string[]; message: string }> =>
    (await apiService.post('/admin/email/import-html', { html })).data,

  // Plantillas
  templates: async (): Promise<{ data: EmailTemplate[]; variables: Record<string, string>; test_redirect?: string | null }> =>
    (await apiService.get('/admin/email/templates')).data,

  createTemplate: async (payload: Partial<EmailTemplate>) =>
    (await apiService.post('/admin/email/templates', payload)).data,

  updateTemplate: async (id: number, payload: Partial<EmailTemplate>) =>
    (await apiService.put(`/admin/email/templates/${id}`, payload)).data,

  deleteTemplate: async (id: number) =>
    (await apiService.delete(`/admin/email/templates/${id}`)).data,
};

export const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Borrador',
  scheduled: 'Programada',
  sending: 'Enviando',
  paused: 'Pausada',
  sent: 'Enviada',
  cancelled: 'Cancelada',
};

export default emailApi;
